// SIDEBAR STATE (sidebar-and-logo, owner 2026-09-30).
//
// Three layouts, chosen by viewport width:
//   full  (>= 1280)  labels + icons
//   rail  (768-1279) icons only, label in a tooltip
//   phone (< 768)    no sidebar on screen: a slide-in menu behind a top-bar button
//
// The user's own collapse/expand choice is remembered per browser and, when
// there is one, wins over the width default on full and rail. On a phone it is
// ignored: the slide-in menu always shows labels.
//
// localStorage is guarded by try/catch on EVERY access: it can throw or come
// back empty (private windows, blocked site data), and the sidebar must render
// the same without it.

import { useCallback, useEffect, useState } from "react";
import { layout } from "@/styles/tokens";

export const SIDEBAR_PREF_KEY = "anixify.sidebar";
export type SidebarPref = "collapsed" | "expanded";
export type SidebarMode = "full" | "rail" | "phone";

export function modeForWidth(width: number): SidebarMode {
  if (width < layout.sidebarAutoCollapseBelow) return "phone";
  if (width < layout.sidebarFullAbove) return "rail";
  return "full";
}

export function readPref(): SidebarPref | null {
  try {
    const v = window.localStorage.getItem(SIDEBAR_PREF_KEY);
    return v === "collapsed" || v === "expanded" ? v : null;
  } catch {
    return null;
  }
}

export function writePref(pref: SidebarPref): void {
  try {
    window.localStorage.setItem(SIDEBAR_PREF_KEY, pref);
  } catch {
    /* not remembered; the choice still applies for this page load */
  }
}

/** Collapsed to the icon rail? Phone never is (its menu shows labels). */
export function isCollapsed(mode: SidebarMode, pref: SidebarPref | null): boolean {
  if (mode === "phone") return false;
  if (pref) return pref === "collapsed";
  return mode === "rail";
}

// WHICH GROUPS ARE OPEN (sidebar-navigation, 2026-10-31). Remembered per user, so
// two people sharing a browser keep their own menu. Only a press on a group is
// remembered; the group opened for the current page is not, or browsing would
// slowly open everything. Same guard: it must work without localStorage.
export const SIDEBAR_GROUPS_KEY = "anixify.sidebar.groups";
export type GroupPrefs = Record<string, boolean>;

export const groupPrefKey = (uid: number | null): string => `${SIDEBAR_GROUPS_KEY}.${uid ?? "anon"}`;

export function readGroupPrefs(uid: number | null): GroupPrefs {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(groupPrefKey(uid)) ?? "{}");
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter(([, v]) => typeof v === "boolean")) as GroupPrefs;
  } catch {
    return {};
  }
}

export function writeGroupPrefs(uid: number | null, prefs: GroupPrefs): void {
  try {
    window.localStorage.setItem(groupPrefKey(uid), JSON.stringify(prefs));
  } catch {
    /* not remembered; the choice still applies for this page load */
  }
}

/** The group holding the current page is open unless the user closed it on this
 *  page; any other group follows the user's choice, then its default. */
export function isGroupOpen(
  group: { id: string; defaultOpen: boolean }, prefs: GroupPrefs, activeId: string | null, dismissed: boolean,
): boolean {
  if (group.id === activeId && !dismissed) return true;
  return prefs[group.id] ?? group.defaultOpen;
}

export function useNavGroups(uid: number | null, activeId: string | null, pathname: string) {
  const [prefs, setPrefs] = useState<GroupPrefs>(() => readGroupPrefs(uid));
  const [dismissed, setDismissed] = useState(false);

  // The session's uid can arrive after the first render.
  useEffect(() => { setPrefs(readGroupPrefs(uid)); }, [uid]);
  useEffect(() => { setDismissed(false); }, [pathname]);

  const isOpen = useCallback(
    (group: { id: string; defaultOpen: boolean }) => isGroupOpen(group, prefs, activeId, dismissed),
    [prefs, activeId, dismissed],
  );
  const setOpen = useCallback((groupId: string, open: boolean) => {
    const next = { ...prefs, [groupId]: open };
    writeGroupPrefs(uid, next);
    setPrefs(next);
    if (groupId === activeId) setDismissed(!open);
  }, [prefs, uid, activeId]);

  return { isOpen, setOpen };
}

const currentWidth = () => (typeof window === "undefined" ? layout.sidebarFullAbove : window.innerWidth);

export function useSidebarState() {
  const [mode, setMode] = useState<SidebarMode>(() => modeForWidth(currentWidth()));
  const [pref, setPref] = useState<SidebarPref | null>(() => readPref());
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setMode(modeForWidth(window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Leaving the phone layout closes a menu that was open.
  useEffect(() => { if (mode !== "phone") setDrawerOpen(false); }, [mode]);

  const collapsed = isCollapsed(mode, pref);
  const toggle = useCallback(() => {
    const next: SidebarPref = isCollapsed(mode, pref) ? "expanded" : "collapsed";
    writePref(next);
    setPref(next);
  }, [mode, pref]);

  return {
    mode, collapsed, toggle,
    drawerOpen,
    openDrawer: useCallback(() => setDrawerOpen(true), []),
    closeDrawer: useCallback(() => setDrawerOpen(false), []),
  };
}
